/**
 * SVG Icons for PDF rendering using @react-pdf/renderer
 * These use native SVG primitives, not icon fonts
 */
import { Svg, Path, Circle, G, Line, Rect, Polyline } from '@react-pdf/renderer';

// Icon size constants
const ICON_SIZE = 16;
const SMALL_ICON = 12;

// CheckCircle - Green checkmark in circle
export const CheckCircleIcon = ({ size = ICON_SIZE, color = '#16a34a' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" fill="none" />
    <Path d="M9 12l2 2 4-4" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// AlertTriangle - Yellow/orange warning triangle
export const AlertTriangleIcon = ({ size = ICON_SIZE, color = '#f59e0b' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path 
      d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" 
      stroke={color} 
      strokeWidth="2" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
    <Line x1="12" y1="9" x2="12" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <Circle cx="12" cy="17" r="1" fill={color} />
  </Svg>
);

// XCircle - Red X in circle
export const XCircleIcon = ({ size = ICON_SIZE, color = '#dc2626' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" fill="none" />
    <Line x1="15" y1="9" x2="9" y2="15" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <Line x1="9" y1="9" x2="15" y2="15" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

// Target - Crosshair/bullseye
export const TargetIcon = ({ size = ICON_SIZE, color = '#64748b' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" fill="none" />
    <Circle cx="12" cy="12" r="6" stroke={color} strokeWidth="2" fill="none" />
    <Circle cx="12" cy="12" r="2" stroke={color} strokeWidth="2" fill="none" />
    <Line x1="22" y1="12" x2="18" y2="12" stroke={color} strokeWidth="2" />
    <Line x1="6" y1="12" x2="2" y2="12" stroke={color} strokeWidth="2" />
    <Line x1="12" y1="6" x2="12" y2="2" stroke={color} strokeWidth="2" />
    <Line x1="12" y1="22" x2="12" y2="18" stroke={color} strokeWidth="2" />
  </Svg>
);

// AlertCircle - Warning/info circle with !
export const AlertCircleIcon = ({ size = ICON_SIZE, color = '#dc2626' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" fill="none" />
    <Line x1="12" y1="8" x2="12" y2="12" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <Circle cx="12" cy="16" r="1" fill={color} />
  </Svg>
);

// TrendingUp - Arrow going up
export const TrendingUpIcon = ({ size = ICON_SIZE, color = '#16a34a' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Polyline points="23,6 13.5,15.5 8.5,10.5 1,18" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <Polyline points="17,6 23,6 23,12" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// TrendingDown - Arrow going down
export const TrendingDownIcon = ({ size = ICON_SIZE, color = '#dc2626' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Polyline points="23,18 13.5,8.5 8.5,13.5 1,6" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <Polyline points="17,18 23,18 23,12" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// Calendar
export const CalendarIcon = ({ size = ICON_SIZE, color = '#64748b' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke={color} strokeWidth="2" fill="none" />
    <Line x1="16" y1="2" x2="16" y2="6" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <Line x1="8" y1="2" x2="8" y2="6" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <Line x1="3" y1="10" x2="21" y2="10" stroke={color} strokeWidth="2" />
  </Svg>
);

// User icon
export const UserIcon = ({ size = ICON_SIZE, color = '#64748b' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx="12" cy="7" r="4" stroke={color} strokeWidth="2" fill="none" />
    <Path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
  </Svg>
);

// FileText - Document icon
export const FileTextIcon = ({ size = ICON_SIZE, color = '#64748b' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <Polyline points="14,2 14,8 20,8" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <Line x1="16" y1="13" x2="8" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <Line x1="16" y1="17" x2="8" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <Line x1="10" y1="9" x2="8" y2="9" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

// Star icon for kudos
export const StarIcon = ({ size = ICON_SIZE, color = '#f59e0b' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path 
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" 
      stroke={color} 
      strokeWidth="2" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
  </Svg>
);

// Clock icon
export const ClockIcon = ({ size = ICON_SIZE, color = '#64748b' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" fill="none" />
    <Polyline points="12,6 12,12 16,14" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// Flag icon for milestones
export const FlagIcon = ({ size = ICON_SIZE, color = '#64748b' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <Line x1="4" y1="22" x2="4" y2="15" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

export default {
  CheckCircleIcon,
  AlertTriangleIcon,
  XCircleIcon,
  TargetIcon,
  AlertCircleIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  CalendarIcon,
  UserIcon,
  FileTextIcon,
  StarIcon,
  ClockIcon,
  FlagIcon,
};
